import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SIZE_LABELS, sizeFromHours } from '@rundown/domain';
import { Button, EmptyState, Modal, PageContainer, PageHeader } from '../../components';
import { cx } from '../../lib/cx';
import { formatHours } from '../../lib/format';
import { useRequiredWorkspace, useSizeThresholds } from '../workspace/useWorkspace';
import { useCapability } from '../workspace/useCapability';
import { projectionQueryOptions } from '../backlog/api';
import { updateRequest } from '../requests/api';
import { TYPE_LABELS } from '../requests/labels';
import { useSearch } from '../search/useSearch';
import { SectionLabel, StatusText } from '../sprints/parts';
import { sprintDate } from '../sprints/sprintMeta';
import {
  createProject,
  deliveryProjectsQueryOptions,
  invalidateProjects,
  updateProject,
  type DeliveryProject,
  type DeliveryProjectRequest,
} from './api';
import {
  inDeliveryOrder,
  partTiming,
  projectProgress,
  type PartPhase,
  type PartTiming,
  type ProjectionView,
} from './partTiming';

/**
 * Projects — the bigger deliverables, and the parts they ship in.
 *
 * A project is not a task list. It is a piece of work like UI Revamp, Address
 * book, Pinpoint replacement or AI Workflows: it runs over months and is
 * delivered in parts, each of which is a request (v0.3 §4: "a larger piece of
 * work split into parts, e.g. AI Calling – Part 1, Part 2").
 *
 * What the screen answers, per project (v0.3 D4, Spec v0.5 §4):
 *
 *  - How far along is it? Parts released, hours delivered, hours to go.
 *  - When does the rest land? Each part says which sprint it is in or
 *    projected into, and the parts read in delivery order — what shipped, what
 *    is running, what is coming — so a project is its own timeline.
 *  - How much did it move after it started? Changes raised after the first
 *    part's sprint locked, with their hours. The sheet this replaces recorded
 *    that only as a note: "modified to include approval calls on last review".
 *
 * The vendor creates projects and groups requests into them; a client reads the
 * same screen without the controls. Spec v0.5 §1 had it as "either side", which
 * it stopped being when month end started splitting by project — see the
 * capability's own comment in @rundown/policy.
 */
export function ProjectsPage() {
  const { workspace } = useRequiredWorkspace();
  const canManage = useCapability('project.manage');
  const query = useQuery(deliveryProjectsQueryOptions(workspace.id));
  const projectionQuery = useQuery(projectionQueryOptions(workspace.id, 'team', 'strict-rank'));
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const projection = useMemo<ProjectionView>(() => {
    const data = projectionQuery.data;
    return {
      landing: new Map((data?.landings ?? []).map((landing) => [landing.itemId, landing])),
      sprints: new Map(
        (data?.sprints ?? []).map((sprint) => [sprint.key, { name: sprint.name, startsOn: sprint.startsOn }]),
      ),
    };
  }, [projectionQuery.data]);

  const all = query.data ?? [];
  const archivedCount = all.filter((project) => project.archived).length;
  const visible = all.filter((project) => showArchived || !project.archived);

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Projects"
        description={
          <>
            The bigger pieces of work, like UI Revamp or AI Workflows, and the parts they ship in. Each part
            is a request, planned into sprints like any other, so a project fills in over time.
          </>
        }
        actions={
          <>
            {archivedCount > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setShowArchived((value) => !value)}>
                {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
              </Button>
            )}
            {canManage && (
              <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                New project
              </Button>
            )}
          </>
        }
      />

      {query.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : query.error !== null ? (
        <EmptyState title="Could not load projects" description={query.error.message} />
      ) : visible.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description="A project groups the requests that make up one bigger deliverable, delivered in parts over time."
          action={
            canManage ? (
              <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                New project
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {sortProjects(visible, projection).map(({ project, parts }) => (
            <ProjectCard
              key={project.id}
              workspaceId={workspace.id}
              project={project}
              parts={parts}
              canManage={canManage}
            />
          ))}
        </div>
      )}

      {creating && <CreateProjectModal workspaceId={workspace.id} onClose={() => setCreating(false)} />}
    </PageContainer>
  );
}

interface TimedPart {
  readonly part: DeliveryProjectRequest;
  readonly timing: PartTiming;
}

const projectRank = (project: DeliveryProject, parts: readonly TimedPart[]): number => {
  if (project.archived) return 3;
  if (parts.length > 0 && parts.every((entry) => entry.timing.phase === 'released')) return 2;
  if (parts.some((entry) => entry.timing.phase === 'released' || entry.timing.phase === 'running')) return 0;
  return 1;
};

/** Work in flight first, then work not started, then finished projects, then archived. */
function sortProjects(projects: readonly DeliveryProject[], projection: ProjectionView) {
  return (
    projects
      .map((project) => {
        const parts = inDeliveryOrder(
          project.requests.map((part) => ({ part, timing: partTiming(part, projection) })),
        );
        return { project, parts, rank: projectRank(project, parts) };
      })
      // eslint-disable-next-line unicorn/no-array-sort -- sorting the array `map` just built
      .sort((a, b) => a.rank - b.rank || a.project.name.localeCompare(b.project.name))
  );
}

const DOT: Readonly<Record<PartPhase, string>> = {
  released: 'bg-success',
  running: 'bg-accent',
  committed: 'bg-violet-strong',
  projected: 'bg-transparent ring-[1.5px] ring-inset ring-line-heavy',
  later: 'bg-transparent ring-[1.5px] ring-inset ring-line-stronger',
  unsized: 'bg-transparent ring-[1.5px] ring-inset ring-line-stronger',
};

const WHEN_TEXT: Readonly<Record<PartPhase, string>> = {
  released: 'text-success',
  running: 'text-ink',
  committed: 'text-ink-soft',
  projected: 'text-muted',
  later: 'text-faint',
  unsized: 'text-faint',
};

function ProjectCard({
  workspaceId,
  project,
  parts,
  canManage,
}: {
  workspaceId: string;
  project: DeliveryProject;
  parts: readonly TimedPart[];
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const progress = projectProgress(parts);
  const pct = progress.total === 0 ? 0 : Math.round((progress.released / progress.total) * 100);

  const archive = useMutation({
    mutationFn: (archived: boolean) => updateProject(workspaceId, project.id, { archived }),
    onSettled: () => invalidateProjects(queryClient, workspaceId),
  });
  const removePart = useMutation({
    mutationFn: (requestId: string) => updateRequest(workspaceId, requestId, { projectId: null }),
    onSettled: () => invalidateProjects(queryClient, workspaceId),
  });

  return (
    <section
      className={cx('flex flex-col rounded-xl border border-line p-5', project.archived && 'opacity-70')}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="min-w-0 truncate fs-17 font-bold text-ink">
          {project.name}
          {project.archived && <span className="ml-2 fs-11 font-semibold text-muted">Archived</span>}
        </h2>
        <span className="fs-12 whitespace-nowrap text-muted">
          {progress.released} of {progress.total} released
        </span>
      </div>

      <div className="mt-2.5 mb-1.5 h-1 overflow-hidden rounded-full bg-surface-3">
        <div className="h-full rounded-full bg-success transition-[width]" style={{ width: `${pct}%` }} />
      </div>
      <p className="mb-4 fs-12 text-muted">
        {project.startedAt !== null
          ? `Started ${sprintDate(project.startedAt.slice(0, 10))} · `
          : parts.some((entry) => entry.timing.phase === 'released' || entry.timing.phase === 'running')
            ? ''
            : 'Not started · '}
        ~{formatHours(progress.deliveredHours)} delivered · ~{formatHours(progress.remainingHours)} to go
        {progress.lastLanding !== null && ` · last part ${progress.lastLanding}`}
        {progress.unscheduled > 0 && ` · ${progress.unscheduled} not scheduled yet`}
      </p>

      {parts.length === 0 ? (
        <p className="mb-4 rounded-lg bg-surface-2 px-3 py-3 fs-12 text-muted">
          No parts yet. Add the requests that make up this deliverable.
        </p>
      ) : (
        <ul className="mb-4 flex flex-col">
          {parts.map(({ part, timing }) => (
            <PartRow
              key={part.id}
              part={part}
              timing={timing}
              onRemove={canManage && !project.archived ? () => removePart.mutate(part.id) : undefined}
            />
          ))}
        </ul>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-surface-3 pt-3 fs-12 text-muted">
        <span>
          Changes after start:{' '}
          <strong className={project.changesAfterStart > 0 ? 'text-violet' : 'text-ink'}>
            {project.changesAfterStart}
          </strong>
          {project.changesAfterStart > 0 && ` · ~${formatHours(project.changeHoursAfterStart)}`}
        </span>
        {canManage && (
          <span className="ml-auto flex items-center gap-3">
            {!project.archived && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="font-semibold text-accent hover:text-accent-strong"
              >
                + Add part
              </button>
            )}
            <button type="button" onClick={() => setRenaming(true)} className="hover:text-ink">
              Rename
            </button>
            <button
              type="button"
              disabled={archive.isPending}
              onClick={() => archive.mutate(!project.archived)}
              className="hover:text-ink"
            >
              {project.archived ? 'Restore' : 'Archive'}
            </button>
          </span>
        )}
      </div>
      {(archive.error ?? removePart.error) !== null && (
        <p role="alert" className="mt-2 fs-12 text-danger">
          {(archive.error ?? removePart.error)?.message}
        </p>
      )}

      {adding && (
        <AddPartModal workspaceId={workspaceId} project={project} onClose={() => setAdding(false)} />
      )}
      {renaming && (
        <RenameProjectModal workspaceId={workspaceId} project={project} onClose={() => setRenaming(false)} />
      )}
    </section>
  );
}

function PartRow({
  part,
  timing,
  onRemove,
}: {
  part: DeliveryProjectRequest;
  timing: PartTiming;
  onRemove?: () => void;
}) {
  const sizeThresholds = useSizeThresholds();
  return (
    <li className="group flex items-center gap-2.5 border-b border-surface-3 py-2 last:border-b-0">
      <span className={cx('size-2 flex-none rounded-full', DOT[timing.phase])} aria-hidden />
      <div className="min-w-0 flex-1">
        <Link to={`/requests/${part.id}`} className="block truncate fs-13 text-ink hover:text-accent">
          {part.title}
        </Link>
        <div className="truncate fs-11 text-muted">
          #{part.number} · {TYPE_LABELS[part.type]}
          {part.approxHours > 0
            ? ` · ${SIZE_LABELS[sizeFromHours(part.approxHours, sizeThresholds)]} ~${formatHours(part.approxHours)}`
            : ''}
          {timing.phase === 'running' && ' · '}
          {timing.phase === 'running' && <StatusText status={part.status} className="fs-11" />}
        </div>
      </div>
      <span className={cx('fs-12 font-semibold whitespace-nowrap', WHEN_TEXT[timing.phase])}>
        {timing.label}
      </span>
      {onRemove !== undefined && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove #${part.number} from this project`}
          title="Remove from this project"
          className="w-5 flex-none text-faint opacity-0 group-hover:opacity-100 hover:text-danger focus:opacity-100"
        >
          ×
        </button>
      )}
    </li>
  );
}

function CreateProjectModal({ workspaceId, onClose }: { workspaceId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => createProject(workspaceId, name.trim()),
    onSuccess: async () => {
      await invalidateProjects(queryClient, workspaceId);
      onClose();
    },
  });
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="New project"
      description="A bigger deliverable, shipped in parts — like UI Revamp or Pinpoint replacement."
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={name.trim() === '' || create.isPending}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() !== '') create.mutate();
        }}
      >
        <input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Project name"
          className="w-full rounded-md border border-line px-3 py-2 fs-13"
        />
      </form>
      {create.error !== null && (
        <p role="alert" className="mt-2 fs-12 text-danger">
          {create.error.message}
        </p>
      )}
    </Modal>
  );
}

function RenameProjectModal({
  workspaceId,
  project,
  onClose,
}: {
  workspaceId: string;
  project: DeliveryProject;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(project.name);
  const rename = useMutation({
    mutationFn: () => updateProject(workspaceId, project.id, { name: name.trim() }),
    onSuccess: async () => {
      await invalidateProjects(queryClient, workspaceId);
      onClose();
    },
  });
  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Rename project"
      footer={
        <>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={name.trim() === '' || rename.isPending}
            onClick={() => rename.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() !== '') rename.mutate();
        }}
      >
        <input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="w-full rounded-md border border-line px-3 py-2 fs-13"
        />
      </form>
      {rename.error !== null && (
        <p role="alert" className="mt-2 fs-12 text-danger">
          {rename.error.message}
        </p>
      )}
    </Modal>
  );
}

/**
 * Add a part: find an existing request and group it under this project. A
 * part is a request raised the normal way (the ask box), so this attaches work
 * rather than creating it. A request belongs to one project; adding one that
 * is already in another moves it here.
 */
function AddPartModal({
  workspaceId,
  project,
  onClose,
}: {
  workspaceId: string;
  project: DeliveryProject;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const search = useSearch(text);
  const inProject = new Set(project.requests.map((part) => part.id));
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());

  const add = useMutation({
    mutationFn: (requestId: string) => updateRequest(workspaceId, requestId, { projectId: project.id }),
    onSuccess: (_row, requestId) => setAdded((current) => new Set(current).add(requestId)),
    onSettled: () => invalidateProjects(queryClient, workspaceId),
  });

  const hits = search.data?.requests ?? [];

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Add parts to ${project.name}`}
      description="Find the requests that make up this deliverable. New work is raised from Home as usual, then added here."
      footer={
        <Button size="sm" variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <input
        autoFocus
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Search by title, #number or project"
        className="mb-3 w-full rounded-md border border-line px-3 py-2 fs-13"
      />
      {text.trim() === '' ? (
        <p className="fs-12 text-muted">Start typing to find a request.</p>
      ) : hits.length === 0 && !search.isFetching ? (
        <p className="fs-12 text-muted">Nothing matches.</p>
      ) : (
        <ul className="flex flex-col">
          {hits.map((hit) => {
            const already = inProject.has(hit.id) || added.has(hit.id);
            const title = hit.title;
            return (
              <li
                key={hit.id}
                className="flex items-center gap-3 border-b border-surface-3 py-2 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate fs-13 text-ink">{title}</div>
                  <div className="fs-11 text-muted">
                    #{hit.number} · {TYPE_LABELS[hit.type]}
                    {hit.projectName !== null && ` · ${hit.projectName}`}
                  </div>
                </div>
                {already ? (
                  <span className="fs-12 font-semibold text-success">Added</span>
                ) : (
                  <Button size="sm" disabled={add.isPending} onClick={() => add.mutate(hit.id)}>
                    Add
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {add.error !== null && (
        <p role="alert" className="mt-2 fs-12 text-danger">
          {add.error.message}
        </p>
      )}
      <SectionLabel className="mt-4">
        {project.requests.length + added.size} part{project.requests.length + added.size === 1 ? '' : 's'} in
        this project
      </SectionLabel>
    </Modal>
  );
}
